import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, X, Download } from "lucide-react";
import { useSettings } from "@/lib/settings";
import { mediaUrl } from "@/lib/media";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Section } from "@/components/Section";

export default function Products() {
  const { settings } = useSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ brands: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [brand, setBrand] = useState(searchParams.get("brand") || "");
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [maxPrice, setMaxPrice] = useState(searchParams.get("max_price") || "");
  const [mobileFilters, setMobileFilters] = useState(false);

  // Keep URL params in sync with filter state (so submenu deep-links persist on share/refresh)
  useEffect(() => {
    const next = {};
    if (q) next.q = q;
    if (brand) next.brand = brand;
    if (category) next.category = category;
    if (maxPrice) next.max_price = maxPrice;
    setSearchParams(next, { replace: true });
  }, [q, brand, category, maxPrice, setSearchParams]);

  // React to URL param changes (e.g. clicking a different sub-menu link without remount)
  useEffect(() => {
    setQ(searchParams.get("q") || "");
    setBrand(searchParams.get("brand") || "");
    setCategory(searchParams.get("category") || "");
    setMaxPrice(searchParams.get("max_price") || "");
    // eslint-disable-next-line
  }, [searchParams.toString()]);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (brand) params.brand = brand;
    if (category) params.category = category;
    if (maxPrice) params.max_price = Number(maxPrice);
    try {
      const r = await api.get("/products", { params });
      setItems(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/products-meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, brand, category, maxPrice]);

  const clear = () => { setQ(""); setBrand(""); setCategory(""); setMaxPrice(""); };

  const Filters = () => (
    <aside className="space-y-6 lg:sticky lg:top-28" data-testid="products-filters">
      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Search</label>
        <div className="relative mt-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="e.g. Bull, JCB, excavator"
            className="w-full pl-10 pr-3 py-3 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm"
            data-testid="products-search-input"
          />
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Brand</label>
        <select value={brand} onChange={(e) => setBrand(e.target.value)} className="mt-2 w-full px-3 py-3 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" data-testid="products-brand-select">
          <option value="">All Brands</option>
          {meta.brands.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Category</label>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="mt-2 w-full px-3 py-3 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" data-testid="products-category-select">
          <option value="">All Categories</option>
          {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Max Price (₹)</label>
        <input type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="e.g. 5000000" className="mt-2 w-full px-3 py-3 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" data-testid="products-price-input" />
      </div>
      <button onClick={clear} className="text-xs uppercase tracking-widest font-bold text-[#E65C00] hover:underline" data-testid="products-clear-filters">
        Clear Filters
      </button>
    </aside>
  );

  return (
    <>
      <section className="relative bg-[#1A1A1A] text-white py-16 noise-overlay overflow-hidden" data-testid="products-hero">
        {settings?.products_hero_image && (
          <>
            <img src={mediaUrl(settings.products_hero_image)} alt="" className="absolute inset-0 w-full h-full object-cover object-center opacity-60" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/95 via-[#1A1A1A]/70 to-[#1A1A1A]/30" />
          </>
        )}
        <Section className="relative">
          <div>
            <div className="label-eyebrow mb-4">Catalog</div>
            <h1 className="heading-xl text-4xl sm:text-5xl lg:text-6xl">PRODUCTS<span className="text-[#E65C00]">.</span></h1>
            <p className="mt-3 text-gray-400 max-w-xl">India's heavyweight inventory. Backhoes, excavators, dozers, tippers — all available for buy, sell or rent.</p>
            {settings?.product_catalog_url && (
              <a
                href={mediaUrl(settings.product_catalog_url)}
                target="_blank"
                rel="noreferrer"
                download
                className="mt-6 inline-flex items-center gap-2 bg-[#FFCC00] text-[#1A1A1A] font-heading font-black uppercase tracking-widest text-xs px-5 py-3 hover:bg-[#E65C00] hover:text-white transition-colors"
                data-testid="products-catalog-download"
              >
                <Download className="w-4 h-4" /> Download Product Catalog (PDF)
              </a>
            )}
          </div>
        </Section>
      </section>

      <Section className="py-12 grid lg:grid-cols-[260px_1fr] gap-10">
        <div className="hidden lg:block"><Filters /></div>
        <div className="lg:hidden">
          <button onClick={() => setMobileFilters(true)} className="btn-outline w-full" data-testid="products-mobile-filter-toggle">
            <SlidersHorizontal className="w-4 h-4" /> Filters
          </button>
          {mobileFilters && (
            <div className="fixed inset-0 z-50 bg-white p-6 overflow-y-auto" data-testid="products-mobile-filter-panel">
              <div className="flex justify-between mb-4">
                <div className="font-heading font-black text-xl">FILTERS</div>
                <button onClick={() => setMobileFilters(false)} aria-label="Close filters"><X /></button>
              </div>
              <Filters />
            </div>
          )}
        </div>

        <div>
          <div className="mb-6 text-sm text-gray-600" data-testid="products-count">
            Showing <strong>{items.length}</strong> {items.length === 1 ? "product" : "products"}
          </div>
          {loading ? (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton aspect-[4/5]" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-gray-300" data-testid="products-empty">
              <p className="text-gray-500">No products match your filters.</p>
              <button onClick={clear} className="btn-outline mt-4">Clear Filters</button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {items.map((p) => <ProductCard key={p.id} p={p} />)}
            </div>
          )}
        </div>
      </Section>
    </>
  );
}
